'use client';

import type { UseQueryResult } from '@tanstack/react-query';
import {
  createContext,
  type PropsWithChildren,
  useContext,
  useMemo,
} from 'react';

import { useCartContext } from '@/app/contexts/CartProvider';
import type { ListProduct } from '@/queries/Products';
import { useProductsData } from '@/queries/Products';
import type { RefreshCart_cart_refreshCart_cart_lineItems } from '@/queries/types/RefreshCart';

import useProductsInventory, {
  DEFAULT_PRODUCTS_INVENTORY,
  type UseProductsInventory,
} from '../_hooks/useProductsInventory';
import { useDiscountContext } from './DiscountProvider';

interface PDPValues extends UseProductsInventory {
  chargerCount: number;
  data?: ListProduct[];
  error: unknown;
  isLoadingPdpData: boolean;
  isSuccess: boolean;
  itemCountForIcon: number;
  lineItems: (RefreshCart_cart_refreshCart_cart_lineItems | null)[];
  maxChargerQuantity: number;
  maxRingQuantity: number;
  ringCount: number;
}

export const INITIAL_PDP_VALUES: PDPValues = {
  chargerCount: 0,
  data: [],
  error: null,
  isLoadingPdpData: true,
  isSuccess: false,
  itemCountForIcon: 0,
  lineItems: [],
  maxChargerQuantity: 10,
  maxRingQuantity: 3,
  ringCount: 0,
  ...DEFAULT_PRODUCTS_INVENTORY,
};

type PDPProvider = PDPValues & UseQueryResult<ListProduct[], Error>;
export type PDPContextType =
  | (PDPProvider & UseProductsInventory)
  | (PDPValues & UseProductsInventory);

const PDPContext = createContext<PDPContextType>(INITIAL_PDP_VALUES);

export const usePDPContext = (): PDPContextType => {
  return useContext(PDPContext);
};

const Provider = ({ children }: PropsWithChildren) => {
  const inventory = useProductsInventory();
  // get cart
  const {
    cartSettings,
    cartSettingsError,
    cartSettingsLoading,
    refreshCart,
    refreshCartError,
    refreshCartLoading,
  } = useCartContext();
  const { hasDiscount } = useDiscountContext();
  const cart = refreshCart?.cart;

  if (cartSettingsError) {
    // eslint-disable-next-line no-console
    console.error('Error fetching cart settings', cartSettingsError);
  }

  if (refreshCartError) {
    // eslint-disable-next-line no-console
    console.error('Error fetching cart', refreshCartError);
  }

  // get products
  const products = useProductsData({
    enabled: hasDiscount,
    includeInventory: true,
  });

  const isLoadingPdpData =
    cartSettingsLoading || refreshCartLoading || products.isLoading;

  const values: PDPProvider = useMemo(() => {
    const { chargerCount, itemCountForIcon, lineItems, ringCount } =
      cart ?? INITIAL_PDP_VALUES;

    const { maxChargerQuantity, maxRingQuantity } =
      cartSettings ?? INITIAL_PDP_VALUES;

    return {
      chargerCount: chargerCount!,
      itemCountForIcon: itemCountForIcon!,
      lineItems: lineItems!,
      maxChargerQuantity: maxChargerQuantity,
      maxRingQuantity: maxRingQuantity,
      ringCount: ringCount!,
      isLoadingPdpData,
      ...products,
      ...inventory,
    };
  }, [cart, cartSettings, inventory, isLoadingPdpData, products]);

  return <PDPContext.Provider value={values}>{children}</PDPContext.Provider>;
};

export default Provider;
